import * as React from 'react';
import { Recommendation, RecommendationSkeleton } from '../../../components/Recommendation';
import { FavoriteButtonContainer } from '../../FavoriteButtonContainer';
import { LabelsContainer } from '../../LabelsContainer';
import { RecommendationsList } from '../../../components/RecommendationsList';
import * as styles from './RecommendationsListMobile.css';
import { EPaginationStatus } from '../../../slices/recommendationsSlice';
import { PikPromoBannerContainer } from '../../PikPromoBannerContainer';

import { useInfiniteRecommendations } from '../useInfiniteRecommendations';
import type { useRecommendations } from '../useRecommendations';
import { ActionLink, UIText1 } from '@cian/ui-kit';
import { getAdjacentTrapPositionInTwoColumnGrid } from '../../../utils/recommendations/getAdjacentTrapPositionInTwoColumnGrid';

type TRecommendationsListMobileProps = {
  onTrack: (offerId: number | null, position: number, contextToken?: string) => void;
  recommendationsControls: ReturnType<typeof useRecommendations>;
  infiniteScroll?: boolean;
};

type TRecommendationCardsProps = Pick<
  ReturnType<typeof useRecommendations>,
  'recommendations' | 'onRecommendationClick' | 'onShowTrap' | 'isOfferVisited'
> & {
  onTrack: (offerId: number | null, position: number, contextToken?: string) => void;
};

type TRecommendationsPaginationErrorProps = {
  onRetry: () => void;
};

const PIK_BANNER_POSITION = 20;

export const RecommendationsPaginationError: React.FC<TRecommendationsPaginationErrorProps> = ({ onRetry }) => (
  <div className={styles['pagination-error']}>
    <UIText1 color="text-secondary-default">Произошла ошибка</UIText1>
    <span className={styles['pagination-error-button']}>
      <ActionLink type="button" onClick={onRetry} fontWeight="bold">
        Попробовать снова
      </ActionLink>
    </span>
  </div>
);

const RecommendationCards: React.FC<TRecommendationCardsProps> = ({
  recommendations,
  onTrack,
  onRecommendationClick,
  onShowTrap,
  isOfferVisited,
}) => (
  <>
    {recommendations.map((recommendation, index) => (
      <React.Fragment key={`recommendation-${index}`}>
        <Recommendation
          onTrack={onTrack}
          position={index + 1}
          onClick={onRecommendationClick}
          onShowTrap={onShowTrap}
          favoriteButton={<FavoriteButtonContainer size="XS" id={recommendation.offerId} />}
          labels={<LabelsContainer visited={isOfferVisited(recommendation.offerId)} />}
          adjacentTrapPosition={getAdjacentTrapPositionInTwoColumnGrid(recommendations, index)}
          {...recommendation}
        />
        {index === PIK_BANNER_POSITION - 1 && <PikPromoBannerContainer />}
      </React.Fragment>
    ))}
  </>
);

type TInfiniteScrollRecommendationsProps = Pick<
  ReturnType<typeof useRecommendations>,
  'onShow' | 'showHeader' | 'hasMore' | 'paginationStatus' | 'onLoadMore' | 'onRetryLoadMore'
> &
  TRecommendationCardsProps;

const InfiniteScrollRecommendations: React.FC<TInfiniteScrollRecommendationsProps> = ({
  onShow,
  showHeader,
  hasMore,
  paginationStatus,
  onLoadMore,
  onRetryLoadMore,
  recommendations,
  onTrack,
  onRecommendationClick,
  onShowTrap,
  isOfferVisited,
}) => {
  const { sentinelRef } = useInfiniteRecommendations({
    onShow,
    recommendations,
    hasMore,
    paginationStatus,
    onLoadMore,
  });

  const recommendationsMobileSkeleton = React.useMemo(
    () =>
      new Array(8)
        .fill(0)
        .map((_, index) => <RecommendationSkeleton key={`recommendation-mobile-pagination-skeleton-${index}`} />),
    [],
  );

  return (
    <RecommendationsList onShow={onShow} withHeader={showHeader}>
      <RecommendationCards
        recommendations={recommendations}
        onTrack={onTrack}
        onRecommendationClick={onRecommendationClick}
        onShowTrap={onShowTrap}
        isOfferVisited={isOfferVisited}
      />
      {paginationStatus === EPaginationStatus.Loading && recommendationsMobileSkeleton}
      {paginationStatus === EPaginationStatus.Error && <RecommendationsPaginationError onRetry={onRetryLoadMore} />}
      {paginationStatus !== EPaginationStatus.Error && hasMore && (
        <div ref={sentinelRef} className={styles['pagination-sentinel']} />
      )}
    </RecommendationsList>
  );
};

export const RecommendationsListMobile: React.FC<TRecommendationsListMobileProps> = props => {
  const {
    recommendationsControls: {
      onShow,
      onMoreClick,
      showHeader,
      recommendations,
      onRecommendationClick,
      onShowTrap,
      isOfferVisited,
      hasMore,
      paginationStatus,
      onLoadMore,
      onRetryLoadMore,
    },
    onTrack,
    infiniteScroll,
  } = props;

  if (infiniteScroll) {
    return (
      <InfiniteScrollRecommendations
        onShow={onShow}
        showHeader={showHeader}
        hasMore={hasMore}
        paginationStatus={paginationStatus}
        onLoadMore={onLoadMore}
        onRetryLoadMore={onRetryLoadMore}
        recommendations={recommendations}
        onTrack={onTrack}
        onRecommendationClick={onRecommendationClick}
        onShowTrap={onShowTrap}
        isOfferVisited={isOfferVisited}
      />
    );
  }

  return (
    <RecommendationsList onShow={onShow} onMoreClick={onMoreClick} withHeader={showHeader} withBottomMargin>
      <RecommendationCards
        recommendations={recommendations}
        onTrack={onTrack}
        onRecommendationClick={onRecommendationClick}
        onShowTrap={onShowTrap}
        isOfferVisited={isOfferVisited}
      />
    </RecommendationsList>
  );
};
